import type { RestProduct } from "@KassaroAB/shared/types/product";
import type { RestUpdateProduct } from "@KassaroAB/shared/types/product";
import type { RestProductGroup } from "@KassaroAB/shared/types/productGroup";
import type { ProductGroupId } from "@KassaroAB/shared/types/ids";

export const isProductGroupFieldLocked = (
  fieldName: keyof RestProductGroup,
  group: RestProductGroup | null | undefined,
): boolean => {
  if (!group) {
    return false;
  }
  const value = group[fieldName];
  if (value === null || value === undefined) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
};

export const getProductGroupLockedValue = (
  fieldName: keyof RestProductGroup,
  group: RestProductGroup | null | undefined,
): unknown => {
  if (!group) {
    return null;
  }
  return group[fieldName];
};

const GROUP_LOCKED_PRODUCT_FIELDS: Partial<
  Record<keyof RestUpdateProduct, keyof RestProductGroup>
> = {
  VAT: "VAT",
  type: "type",
  unitId: "unitId",
  courseId: "courseId",
  bookkeepingAccountId: "bookkeepingAccountId",
  takeawayVAT: "takeawayVAT",
  takeawayBookkeepingAccountId: "takeawayBookkeepingAccountId",
  modifierGroups: "modifierGroups",
  modifiers: "modifiers",
  alwaysAppliedModifiers: "alwaysAppliedModifiers",
};

export const getEffectiveProductGroupId = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
): ProductGroupId | null => {
  if (edits && "groupId" in edits) {
    return edits.groupId ?? null;
  }
  return product.groupId ?? null;
};

export const getProductGroupForRow = (
  product: RestProduct,
  edits: RestUpdateProduct | undefined,
  productGroupsById: ReadonlyMap<ProductGroupId, RestProductGroup>,
): RestProductGroup | undefined => {
  const groupId = getEffectiveProductGroupId(product, edits);
  if (!groupId) {
    return undefined;
  }
  return productGroupsById.get(groupId);
};

export const stripGroupLockedEdits = (
  edits: RestUpdateProduct,
  group: RestProductGroup | undefined,
): RestUpdateProduct => {
  if (!group) {
    return edits;
  }
  const result = { ...edits };
  (
    Object.entries(GROUP_LOCKED_PRODUCT_FIELDS) as [
      keyof RestUpdateProduct,
      keyof RestProductGroup,
    ][]
  ).forEach(([productField, groupField]) => {
    if (
      productField in result &&
      isProductGroupFieldLocked(groupField, group)
    ) {
      delete result[productField];
    }
  });
  return result;
};
